import { questionTypes } from "../pages/ClientConnectV2/survey/SurveyPreviewBody";
import { normalizeId } from "../pages/ClientConnectV2/survey/surveyPreviewUtils";
import { getSelectedOptions } from "../pages/ClientConnectV2/helpers/clientConnectSurveyQuestions";
import { isGridspotQuestion, toGridspotAnswerValue } from "./gridspot";

const QUESTION_TYPES = questionTypes;

const SCORE_EXCLUDE_TYPES = new Set([
  QUESTION_TYPES.RADIO,
  QUESTION_TYPES.RATING,
  QUESTION_TYPES.EMOJI,
  QUESTION_TYPES.DROPDOWN,
  QUESTION_TYPES.MULTI,
  QUESTION_TYPES.SKILLS,
  QUESTION_TYPES.SEQUENCE,
]);

function isChoiceValueChanged(previousValue, nextValue) {
  const toSortedIds = (value) => {
    if (value == null || value === "") {
      return [];
    }
    const items = Array.isArray(value) ? value : [value];
    return items.map((id) => normalizeId(id)).sort();
  };

  const previousIds = toSortedIds(previousValue);
  const nextIds = toSortedIds(nextValue);

  if (previousIds.length !== nextIds.length) {
    return true;
  }

  return previousIds.some((id, index) => id !== nextIds[index]);
}

function isSingleChoiceType(questionType) {
  return [
    QUESTION_TYPES.RADIO,
    QUESTION_TYPES.DROPDOWN,
    QUESTION_TYPES.EMPLOYEES,
  ].includes(questionType);
}

/**
 * Same as Normal Radio/Rating/MultiSelect: true when any selected option
 * has score_exclusion_selection.
 */
export function resolveScoreExclude(question, value) {
  if (!question?.options?.length || value == null || value === "") {
    return false;
  }

  return getSelectedOptions(question, value).some((option) =>
    Boolean(option?.score_exclusion_selection),
  );
}

export function shouldClearChoiceImages(
  question,
  nextValue,
  previousValue,
  questionType,
) {
  if (isGridspotQuestion(questionType) || isGridspotQuestion(question)) {
    return true;
  }

  const selected = getSelectedOptions(question, nextValue);
  const needsImage = selected.some((opt) => opt?.image_required);

  if (!needsImage) {
    return true;
  }

  if (!isSingleChoiceType(questionType)) {
    return false;
  }

  return isChoiceValueChanged(previousValue, nextValue);
}

export function getStoredAnswer(answers, questionId) {
  if (!answers) {
    return undefined;
  }

  const answerKey = normalizeId(questionId);
  return answers[answerKey] ?? answers[questionId];
}

function projectAnswerAfterPatch(currentAnswer, patch, question, isChoiceType) {
  const projected = { ...(currentAnswer || {}) };

  if (patch?.value !== undefined) {
    projected.value = isGridspotQuestion(question)
      ? toGridspotAnswerValue(patch.value)
      : patch.value;

    if (question && SCORE_EXCLUDE_TYPES.has(question.questionType)) {
      projected.score_exclude = resolveScoreExclude(question, patch.value);
    }

    if (question && isChoiceType && !isGridspotQuestion(question)) {
      const selected = getSelectedOptions(question, patch.value);
      if (!selected.some((opt) => opt?.write_in)) {
        projected.write_in = "";
      }
      if (
        shouldClearChoiceImages(
          question,
          patch.value,
          currentAnswer?.value,
          question.questionType,
        )
      ) {
        delete projected.image;
      }
    }
  }

  if (patch?.write_in !== undefined) {
    projected.write_in = patch.write_in;
  }

  if (patch?.note !== undefined) {
    projected.note = patch.note;
  }

  if (patch?.image !== undefined) {
    if (patch.image?.length) {
      projected.image = patch.image;
    } else {
      delete projected.image;
    }
  }

  return projected;
}

/**
 * Creates a shared answer handler for survey and learning flows.
 * Dispatches answer mutations via injected action creators; validation is optional.
 */
export function createSurveyAnswerHandler({
  answers,
  questionById,
  addAnswer,
  addWriteIn,
  addNote,
  addImage,
  clearAnswerImage,
  onValidationChange,
  getValidationError,
}) {
  return (questionId, patch) => {
    const answerKey = normalizeId(questionId);
    const question = questionById.get(answerKey);
    const type = question?.questionType;
    const isChoiceType = [
      QUESTION_TYPES.RADIO,
      QUESTION_TYPES.MULTI,
      QUESTION_TYPES.SKILLS,
      QUESTION_TYPES.SEQUENCE,
      QUESTION_TYPES.DROPDOWN,
    ].includes(type);
    if (patch?.value !== undefined) {
      const nextValue =
        type === QUESTION_TYPES.GRIDSPOT
          ? toGridspotAnswerValue(patch.value)
          : patch.value;
      const answerType =
        type === QUESTION_TYPES.GRIDSPOT ||
        type === QUESTION_TYPES.EMPLOYEES ||
        (isChoiceType && !Array.isArray(nextValue))
          ? "id"
          : type;
      const payload = { id: answerKey, value: nextValue, type: answerType };

      if (type === QUESTION_TYPES.FILE || type === QUESTION_TYPES.SIGNATURE) {
        payload.isFile = true;
      }

      if (question && SCORE_EXCLUDE_TYPES.has(type)) {
        payload.score_exclude = resolveScoreExclude(question, nextValue);
      }

      // Where the respondent tapped inside a Gridspot. A spot can cover many cells,
      // so the option id alone does not record the position.
      if (type === QUESTION_TYPES.GRIDSPOT && patch?.gridspot_point) {
        payload.gridspot_point = patch.gridspot_point;
      }

      addAnswer(payload);

      if (question && isChoiceType) {
        const selected = getSelectedOptions(question, nextValue);
        if (!selected.some((opt) => opt?.write_in)) {
          addWriteIn({ id: answerKey, write_in: "", type: answerType });
        }
        if (
          shouldClearChoiceImages(
            question,
            nextValue,
            getStoredAnswer(answers, questionId)?.value,
            type,
          )
        ) {
          clearAnswerImage({ id: answerKey });
        }
      }
    }

    if (patch?.write_in !== undefined) {
      addWriteIn({ id: answerKey, write_in: patch.write_in, type: "text" });
    }

    if (patch?.note !== undefined) {
      addNote?.({ id: answerKey, note: patch.note, type: "text" });
    }

    if (patch?.image !== undefined) {
      if (patch.image?.length) {
        addImage({ id: answerKey, image: patch.image });
      } else {
        clearAnswerImage({ id: answerKey });
      }
    }

    if (onValidationChange && getValidationError) {
      onValidationChange((prev) => {
        const existingError = prev[answerKey] ?? prev[questionId];
        if (!existingError) {
          return prev;
        }

        if (!question) {
          const next = { ...prev };
          delete next[answerKey];
          delete next[questionId];
          return next;
        }

        const projectedAnswer = projectAnswerAfterPatch(
          getStoredAnswer(answers, questionId),
          patch,
          question,
          isChoiceType,
        );
        const error = getValidationError(question, projectedAnswer);

        if (error) {
          return { ...prev, [answerKey]: error };
        }

        const next = { ...prev };
        delete next[answerKey];
        delete next[questionId];
        return next;
      });
    }
  };
}

export { questionTypes as surveyQuestionTypes };
